import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ExchangeCalculator.scss";
import Button from "components/Button/Button";
import CustomInput from "components/CustomInput/CustomInput";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormTab from "components/FormTab/FormTab";
import { ClientType } from "components/Calculator/Calculator";
import exchangeIcon from "../../assets/images/exchangeIcon.svg";
import { useAppSelector, useAppDispatch } from "store";
import { exchangeActions } from "../../store/reducers/exchange";
import { EMAIL_REGEXP } from "constants/validation";
import {
  otcInit,
  getCurrencyAsset,
  takeLeadingConvertCurrency,
} from "store/reducers/exchange";
import CurrencyDropdown from "components/CurrencyDropdown/CurrencyDropdown";
import { CurrencyAssetResponse } from "models/otc";
import AmountInput from "components/AmountInput/AmountInput";
import { scrollToSection } from "utils/scroll";
import { useTranslation } from "react-i18next";
interface ExchangeValueProps {
  clientType: string;
  setClientType: React.Dispatch<React.SetStateAction<ClientType>>;
  tabArray: ClientType[];
  isIndividualClient: boolean;
}

const ExchangeCalculator = ({
  clientType,
  setClientType,
  tabArray,
  isIndividualClient,
}: ExchangeValueProps) => {
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [isKeepOrdering, setIsKeepOrdering] = useState(true);
  const [isLimitError, setIsLimitError] = useState(false);
  const [isFieldTouched, setIsFieldTouched] = useState(false);
  const [isReceiveCalculation, setIsReceiveCalculation] = useState(true);
  const navigate = useNavigate();
  const { from, to, email, rate, feePercent, isConvert } = useAppSelector(
    (state) => state.exchange
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getCurrencyAsset());
  }, [dispatch]);

  const convertCurrencyFrequency = useCallback(() => {
    dispatch(
      takeLeadingConvertCurrency({
        from: isKeepOrdering ? from.symbol : to.symbol,
        to: isKeepOrdering ? to.symbol : from.symbol,
        amount: isKeepOrdering ? Number(from.amount) : Number(to.amount),
        keepOrdering: isKeepOrdering,
        isReceiveCalculation: isReceiveCalculation,
      })
    );
  }, [
    dispatch,
    from.amount,
    from.symbol,
    to.symbol,
    to.amount,
    isKeepOrdering,
    isReceiveCalculation,
  ]);

  const convertCurrencyAction = useCallback(
    (keepOrdering: boolean) => {
      dispatch(
        takeLeadingConvertCurrency({
          from: keepOrdering ? from.symbol : to.symbol,
          to: keepOrdering ? to.symbol : from.symbol,
          amount: keepOrdering ? Number(from.amount) : Number(to.amount),
          keepOrdering: keepOrdering,
          isReceiveCalculation: isReceiveCalculation,
        })
      );
    },
    [dispatch, from, to, isReceiveCalculation]
  );
  
  useEffect(() => {
    if (Number(from.amount) === 0) return;
    convertCurrencyFrequency();
    const intervalId = setInterval(convertCurrencyFrequency, 5000);
    return () => clearInterval(intervalId);
  }, [dispatch, from, to, convertCurrencyFrequency]); 
  

  useEffect(() => {
    convertCurrencyAction(true);
  }, [dispatch, from.symbol, to.symbol, convertCurrencyAction]);

  const changeRotation = useCallback(() => {
    dispatch(exchangeActions.changeRotation());
    setIsReceiveCalculation(!isReceiveCalculation);
  }, [dispatch, isReceiveCalculation]);

  const setEmail = useCallback(
    (email: string) => {
      dispatch(exchangeActions.setEmail(email));
    },
    [dispatch]
  );

  const setFromAmount = useCallback(
    (amount?: number | string) => {
      dispatch(exchangeActions.setFromAmount(amount));
    },
    [dispatch]
  );
  const setToAmount = useCallback(
    (amount?: number | string) => {
      dispatch(exchangeActions.setToAmount(amount));
    },
    [dispatch]
  );
  const setFromCurrency = useCallback(
    (currency: CurrencyAssetResponse) => {
      dispatch(exchangeActions.setFromCurrency(currency));
    },
    [dispatch]
  );
  const setToCurrency = useCallback(
    (currency: CurrencyAssetResponse) => {
      dispatch(exchangeActions.setToCurrency(currency));
    },
    [dispatch]
  );
  const handleFocus = (setValue: (param: string) => void, value: string) => {
    if (value === "0") setValue("");
  };

  const handleBlur = (value: string, setValue: (param: string) => void) => {
    if (value === "") setValue("0");
  };

  const isEmailValid = EMAIL_REGEXP.test(email);
  const isDisabled = from.amount === 0 || to.amount === 0 || email === "";

  const validatEmail = (email: string) => {
    setEmail(email);
    setIsInvalidEmail(!EMAIL_REGEXP.test(email));
  };

  const onSubmit = async () => {
    if (!isEmailValid) {
      setIsInvalidEmail(true);
      scrollToSection("home");
      return;
    } else if(isDisabled){
      return;
    }
    setIsInvalidEmail(false);

    await dispatch(
      otcInit({
        email: email,
        amount: Number(from.amount),
        from: from.symbol,
        to: to.symbol,
        integrationId: "PayPilotOTCDesk",
        operationType: `${from.type}_TO_${to.type}`,
        customerType: isIndividualClient ? "INDIVIDUAL" : "BUSINESS",
        fromBlockchain: `${from.blockchain}`,
        toBlockchain: `${to.blockchain}`,
      })
    );
    navigate('#form')
  };

  return (
    <div className="exchange-calculator-container">
      <div className="client-type-row">
        {tabArray.map((name, index) => (
          <FormTab
            key={index}
            tabName={t(`exchange.${name}`)}
            isActive={name === clientType}
            setClientType={() => setClientType(name)}
          />
        ))}
      </div>
      <div className="exchange-area">
        <div
          className={
            isLimitError && from.type == "CRYPTO"
              ? "exchange-area-row limit-error"
              : "exchange-area-row"
          }
        >
          <div className="title">{t("exchange.titles.send")}</div>
          <div className="exchange-currency">
            <CurrencyDropdown
              currencies={from.currencies}
              type={from.type}
              value={from}
              onChange={(currency: CurrencyAssetResponse) => {
                setFromCurrency(currency);
              }}
            />
            <div className="exchange-value">
              <AmountInput
                exchange={from}
                setAmount={setFromAmount}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                setIsKeepOrdering={() => setIsKeepOrdering(true)}
                setLimitError={setIsLimitError}
                setIsFieldTouched={setIsFieldTouched}
                isFieldTouched={isFieldTouched}
                setIsReceiveCalculation={() => setIsReceiveCalculation(true)}
              />
              <div>{from.symbol}</div>
            </div>
          </div>
        </div>
        <div
          className="exchange-icon"
          onClick={isConvert ? undefined : changeRotation}
        >
          <img src={exchangeIcon} />
        </div>

        <div
          className={
            isLimitError && to.type == "CRYPTO"
              ? "exchange-area-row limit-error"
              : "exchange-area-row"
          }
        >
          <div className="title">{t("exchange.titles.receive")}</div>
          <div className="exchange-currency">
            <CurrencyDropdown
              currencies={to.currencies}
              type={to.type}
              value={to}
              onChange={(currency: CurrencyAssetResponse) => {
                setToCurrency(currency);
              }}
            />
            <div className="exchange-value">
              <AmountInput
                exchange={to}
                setAmount={setToAmount}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                setIsKeepOrdering={() => setIsKeepOrdering(false)}
                setLimitError={setIsLimitError}
                setIsFieldTouched={setIsFieldTouched}
                isFieldTouched={isFieldTouched}
                setIsReceiveCalculation={() => setIsReceiveCalculation(false)}
              />
              <div>{to.symbol}</div>
            </div>
          </div>
        </div>
      </div>
      <CustomInput
        label={t("exchange.titles.enterEmail")}
        name="email"
        id="email"
        variant="filled"
        className={isInvalidEmail ? "error-filed" : ""}
        onFocus={() => setIsInvalidEmail(false)}
        onChange={(e) => {
          setEmail(e.target.value);
        }}
        onBlur={(e) => {
          setEmail(e.target.value.trim());
          validatEmail(e.target.value.trim());
        }}
        value={email}
      />
      {isInvalidEmail && (
        <div className="error-text">
          <InfoOutlinedIcon />
          {t("exchange.enterValidEmail")}
        </div>
      )}
      <div className="rate-info">
        {t("exchange.rate", {
          from: from.symbol,
          to: to.symbol,
          feePercent: feePercent,
          rate: rate,
        })}
      </div>
      <Button
        btnName={t("buttons.continue")}
        className="blue-btn"
        onClick={() => onSubmit()}
      />
      <div className="info-text">
        <InfoOutlinedIcon />
        {t("exchange.rateIsFixed")}
      </div>
    </div>
  );
};

export default ExchangeCalculator;
